import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Actives, Filter, FilterOp, SortMode, SortOrder } from '@homeleite/arch';
import { EventType } from '@homeleite/domain';
import { AnimalEventsApiRepository } from '../../../../../../../../../libs/domain/src/lib/animals/events/animal-events-api.repository';
import { EventTypeMapper } from '../../../../../../../../../libs/domain/src/lib/animals/events/event-type-mapper';
import { MilkControlsApiRepository } from '../../../../../../../../../libs/domain/src/lib/milk-control/milk-controls-api.repository';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';

type ViewTab = 'history' | 'milking';
type ControlOrigin = 'APP' | 'SRG_SCL' | 'MIXED' | null;

interface EventView {
    id: string;
    date: Date | string;
    type: string;
    label: string;
    color: string;
    iconPath: string | null;
    isMilking: boolean;
    origin: ControlOrigin;
    milkControlId: string | null;
}

/** Uma linha de controle = 1 mês (já compactado; anos agrupam os meses). */
interface MilkingMonthGroup {
    key: string;
    year: number;
    label: string;
    fromLabel: string;
    toLabel: string;
    origin: ControlOrigin;
    originLabel: string;
}

interface YearBucket<T> {
    year: number;
    count: number;
    open: boolean;
    items: T[];
}

@Component({
    selector: 'app-animal-events-dialog',
    imports: [CommonModule, MatDialogModule, MatButtonModule, MatProgressSpinnerModule, MatIconModule],
    templateUrl: './producer-details-animal-events-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./producer-details-animal-events-dialog.component.scss']
})
export class ProducerDetailsAnimalEventsDialogComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private controlOriginById = new Map<string, ControlOrigin>();

    historyEvents: EventView[] = [];
    milkingGroups: MilkingMonthGroup[] = [];
    milkingByYear: YearBucket<MilkingMonthGroup>[] = [];

    tab: ViewTab = 'history';
    isLoading = false;
    hasError = false;

    constructor(
        public dialogRef: MatDialogRef<ProducerDetailsAnimalEventsDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            animalId: string;
            animalName: string;
            animalRegister?: string;
            producerId?: string;
        },
        private eventsRepository: AnimalEventsApiRepository,
        private milkControlsRepository: MilkControlsApiRepository
    ) {}

    ngOnInit(): void {
        this.loadEvents();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    setTab(tab: ViewTab): void {
        this.tab = tab;
    }

    toggleYear(bucket: YearBucket<unknown>): void {
        bucket.open = !bucket.open;
    }

    loadEvents(): void {
        this.isLoading = true;
        this.hasError = false;

        const filters = new Filter('animal.$id', FilterOp.EQ, this.data.animalId);
        const sorting = [new SortOrder('date', SortMode.DSC)];
        const events$ = this.eventsRepository.list('', [filters], sorting, Actives.TRUE);
        const controls$ = this.data.producerId
            ? this.milkControlsRepository.listSummariesByProducer(this.data.producerId).pipe(
                  catchError(() => of([]))
              )
            : of([]);

        forkJoin({ events: events$, controls: controls$ })
            .pipe(
                finalize(() => (this.isLoading = false)),
                takeUntil(this.destroy$)
            )
            .subscribe({
                next: ({ events, controls }) => {
                    this.controlOriginById.clear();
                    for (const c of controls || []) {
                        const origin = this.resolveOrigin(c?.origin);
                        if (c?.id && origin) {
                            this.controlOriginById.set(c.id, origin);
                        }
                    }

                    const all = (events || []).map((event) => this.toView(event));
                    this.historyEvents = all.filter((e) => !e.isMilking);
                    const milking = all.filter((e) => e.isMilking);
                    this.milkingGroups = this.buildMilkingGroups(milking);
                    this.milkingByYear = this.bucketByYear(
                        this.milkingGroups,
                        (g) => g.year,
                        new Date().getFullYear()
                    );

                    if (this.historyEvents.length === 0 && this.milkingGroups.length > 0) {
                        this.tab = 'milking';
                    } else {
                        this.tab = 'history';
                    }
                },
                error: (err) => {
                    console.error('Falha ao carregar eventos:', err);
                    this.hasError = true;
                }
            });
    }

    private toView(event: any): EventView {
        const typeInfo = this.resolveType(event?.type);
        const isMilking =
            typeInfo.name === EventType.MILKED_ANIMAL.name ||
            String(event?.type || '')
                .toLowerCase()
                .includes('milk') ||
            String(event?.type || '').toUpperCase() === 'MILKED_ANIMAL';
        const milkControlId = event?.milkControlId || null;
        const origin =
            this.resolveOrigin(event?.origin) ||
            (milkControlId ? this.controlOriginById.get(milkControlId) || null : null);
        return {
            id: event?.id,
            date: event?.date,
            type: typeInfo.name,
            label: typeInfo.label,
            color: typeInfo.backgroundColor,
            iconPath: typeInfo.iconPath || null,
            isMilking,
            origin,
            milkControlId
        };
    }

    private resolveOrigin(raw: unknown): ControlOrigin {
        const v = String(raw || '')
            .trim()
            .toUpperCase();
        if (v === 'APP') return 'APP';
        if (v === 'SRG_SCL' || v === 'SRG-SCL' || v === 'SRGSCL') return 'SRG_SCL';
        return null;
    }

    private mergeOrigin(current: ControlOrigin, next: ControlOrigin): ControlOrigin {
        if (!next) return current;
        if (!current) return next;
        if (current === next) return current;
        return 'MIXED';
    }

    originClass(origin: ControlOrigin): string {
        if (origin === 'APP') return 'ok';
        if (origin === 'SRG_SCL') return 'warn';
        if (origin === 'MIXED') return 'off';
        return 'off';
    }

    private originLabelOf(origin: ControlOrigin): string {
        if (origin === 'APP') return 'App';
        if (origin === 'SRG_SCL') return 'SRG-SCL';
        if (origin === 'MIXED') return 'Misto';
        return '—';
    }

    private resolveType(name: string): EventType {
        const raw = String(name || '').trim();
        const lower = raw.toLowerCase();
        const cleanName = lower === 'herd_out' || lower === 'herd-out' ? 'herdout' : lower;
        try {
            return EventType.get(cleanName);
        } catch {
            try {
                return EventTypeMapper.getEventBy(raw) || EventTypeMapper.getEventBy(cleanName);
            } catch {
                return new EventType(cleanName || 'unknown', name || 'Evento', null as any, '#8a968c');
            }
        }
    }

    /**
     * Controles: 1 linha por mês (não guarda cada ordenha).
     * Anos ficam em accordion — só o ano corrente/recente abre por padrão.
     */
    private buildMilkingGroups(events: EventView[]): MilkingMonthGroup[] {
        const map = new Map<
            string,
            MilkingMonthGroup & { first?: Date; last?: Date; originAcc: ControlOrigin }
        >();
        const monthFmt = new Intl.DateTimeFormat('pt-BR', {
            month: 'long',
            year: 'numeric',
            timeZone: 'UTC'
        });
        const dayFmt = new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: 'UTC'
        });

        for (const ev of events) {
            const d = ev.date ? new Date(ev.date) : null;
            if (!d || Number.isNaN(d.getTime())) {
                continue;
            }
            const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
            let group = map.get(key);
            if (!group) {
                const label = monthFmt.format(d);
                group = {
                    key,
                    year: d.getUTCFullYear(),
                    label: label.charAt(0).toUpperCase() + label.slice(1),
                    fromLabel: '',
                    toLabel: '',
                    origin: ev.origin,
                    originLabel: '',
                    originAcc: ev.origin,
                    first: d,
                    last: d
                };
                map.set(key, group);
            } else {
                if (d > group.last!) {
                    group.last = d;
                }
                if (d < group.first!) {
                    group.first = d;
                }
                group.originAcc = this.mergeOrigin(group.originAcc, ev.origin);
            }
        }

        return Array.from(map.values()).map((g) => ({
            key: g.key,
            year: g.year,
            label: g.label,
            fromLabel: g.first ? dayFmt.format(g.first) : '—',
            toLabel: g.last ? dayFmt.format(g.last) : '—',
            origin: g.originAcc,
            originLabel: this.originLabelOf(g.originAcc)
        }));
    }

    private bucketByYear<T>(
        items: T[],
        yearOf: (item: T) => number,
        currentYear: number
    ): YearBucket<T>[] {
        const map = new Map<number, T[]>();
        for (const item of items) {
            const y = yearOf(item);
            const list = map.get(y);
            if (list) {
                list.push(item);
            } else {
                map.set(y, [item]);
            }
        }
        const years = Array.from(map.keys()).sort((a, b) => b - a);
        const preferOpen = years.includes(currentYear) ? currentYear : years[0];
        return years.map((year) => ({
            year,
            count: map.get(year)!.length,
            open: year === preferOpen,
            items: map.get(year)!
        }));
    }

    onClose(): void {
        this.dialogRef.close();
    }
}
